import React, { createContext, useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import * as ROUTES from '../config/routes';
import config from "../config/config";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [roles, setRoles] = useState([]);
  const [lastVisitedPage, setLastVisitedPage] = useState(null);
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const location = useLocation();

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Récupération sécurisée des informations depuis le localStorage
    const storedToken = localStorage.getItem('token');
    const storedIsLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const storedRolesString = localStorage.getItem('roles');
    const storedRoles = (storedRolesString && storedRolesString !== 'undefined')
        ? JSON.parse(storedRolesString)
        : [];
    const storedExpiredAt = localStorage.getItem('expired-at');

    if (storedToken) {
      setToken(storedToken);
      setIsLoggedIn(storedIsLoggedIn);
      setRoles(storedRoles);

      const admin = storedRoles && storedRoles.includes('ROLE_ADMIN');
      setIsAdmin(admin);

      // Vérification immédiate de l'expiration du token au chargement
      checkTokenExpiration(storedToken, storedExpiredAt);
    }

    setIsAuthLoaded(true);

    // Gestion de la dernière page visitée pour la redirection après login
    const excludedPages = [ ROUTES.LOGIN_PATH ];
    const isExcluded = excludedPages.some(excludedPath => {
      const regex = new RegExp(`^${excludedPath.replace(/:[^/]+/g, '[^/]+')}$`);
      return regex.test(location.pathname);
    });
    if (!isExcluded) {
      setLastVisitedPage(location.pathname);
    }
  }, [location]);

  // Vérifie si le token expire sous 24 heures et le renouvelle si besoin
  const checkTokenExpiration = async (storedToken, storedExpiredAt) => {
    if (!storedExpiredAt) {
      handleLogout();
    }

    const expirationDate = new Date(storedExpiredAt);
    const now = new Date();
    const diffInHours = (expirationDate - now) / (1000 * 60 * 60);

    // Renouvelle le token s'il expire dans moins de 24 heures
    if (diffInHours <= 24) {
      try {
        const response = await fetch(`${config.apiUrl}token/refresh-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${storedToken}`,
          },
          body: JSON.stringify({ token: storedToken }),
        });

        if (!response.ok) {
          throw new Error('Token refresh failed');
        }
        const data = await response.json();

        if (data.token) {
          // Met à jour l'authentification avec les nouvelles informations
          handleLogin(data);
        }
      } catch (error) {
        handleLogout();
      }
    }
  };

  // Vérification périodique du token (toutes les 10 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const storedToken = localStorage.getItem('token');
      const storedExpiredAt = localStorage.getItem('expired-at');
      if (storedToken && storedExpiredAt) {
        checkTokenExpiration(storedToken, storedExpiredAt);
      }
    }, 1000 * 10);// * 60); // toutes les 10 minutes

    return () => clearInterval(interval);
  }, []);

  const handleLogin = (tokenDatas) => {
    // On s'attend ici à recevoir un objet incluant token, roles et expiredAt (objet renvoyé par l'API)
    setIsLoggedIn(true);
    setToken(tokenDatas.token);
    setRoles(tokenDatas.roles || []);

    setIsAdmin(tokenDatas.roles?.includes('ROLE_ADMIN') || false);

    // Stockage dans le localStorage : on ne garde que la valeur de date pour expiredAt
    localStorage.setItem('token', tokenDatas.token);
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('roles', JSON.stringify(tokenDatas.roles));
    localStorage.setItem('expired-at', tokenDatas.expiredAt.date);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setToken(null);
    setRoles([]);
    setIsAdmin(false);
    localStorage.removeItem('token');
    localStorage.setItem('isLoggedIn', 'false');
    localStorage.removeItem('roles');
  };

  const handleLogout = () => {
    logout();
  };

  const authContextValue = {
    isLoggedIn,
    token,
    roles,
    isAdmin,
    setToken,
    setRoles,
    setIsLoggedIn,
    handleLogin,
    logout,
    handleLogout,
    lastVisitedPage,
    isAuthLoaded
  };

  return (
      <AuthContext.Provider value={authContextValue}>
        {children}
      </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth, AuthContext };
